<!--
 * Path: packages/frontend/src/features/browser/forms/number/NumberField.vue
 * Task: Wrapper for numeric fields (integer, float, currency) handling edit modes.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="field.name"
    :info="field.info"
    :can-edit="canEdit"
    :disable-edit="field.readonly"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="$emit('show-info')"
  >
    <template #read>
      <NumberRead :model-value="fieldValueProxy" :type="field.type" />
    </template>
    <template #edit>
      <NumberEdit v-model="fieldValueProxy" :type="field.type" :error-messages="errorMessages" @save="saveChanges" />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import NumberRead from './NumberRead.vue';
import NumberEdit from './NumberEdit.vue';

interface FieldProps {
  id: string;
  type: string;
  name: string;
  info?: string;
  readonly?: boolean;
}

const props = defineProps<{ field: FieldProps; errorMessages?: string | string[] }>();
const emit = defineEmits<{ (e: 'show-info'): void; }>();

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value && !props.field.readonly);
const activeEntity = computed(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const handleToggleEdit = (newVal: boolean) => {
  if (!newVal) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  await saveEntity(formContext?.isSecondary || false);
  isEditing.value = false;
};

const fieldValueProxy = computed({
  get: () => {
    if (!activeEntity.value || !props.field.id) return null;
    return activeEntity.value[props.field.id];
  },
  set: (newValue: any) => {
    if (activeEntity.value && props.field.id) {
      activeEntity.value[props.field.id] = newValue;
    }
  }
});
</script>
